"use client";

import * as Phaser from 'phaser';
import { useEffect, useRef } from 'react';

// assets
import baseSlineSprite from '../assets/img/line.svg';
import pieceSprite01 from '../assets/img/pieces01.png';
import pieceSprite02 from '../assets/img/pieces02.png';
import dropzoneSprite from '../assets/img/dropzone.svg';
import { Board } from './Board';
import { Card, createDeck, drawCard } from './Cards';
import { GameLogEntry, cellName } from './GameLog';
import { BEGINNER_ACCURACY, pickActivation, pickDiscard, pickMove } from './Bot';
import type { ActivationDecision, BotState, Move as BotMove } from './Bot';
import type { OnlineAction } from '../multiplayer';

const numberOfPiecePerPlayer: number = 3;
const DECK_SIZE = 30;
// a hand can hold at most this many cards; draws past the cap are skipped
const MAX_HAND_SIZE = 3;
// the DOM "ACTIVATE!" overlay plays for this long; the scene waits for it to
// finish before advancing or resuming the turn (must match `card-activate` in
// src/App.css)
export const CARD_ACTIVATE_DURATION_MS = 2200;
// THROW CARD sequencing: the discarded-card reveal starts after the ACTIVATE
// overlay and plays for this long (must match `card-discard` in src/App.css)
export const DISCARD_REVEAL_DELAY_MS = CARD_ACTIVATE_DURATION_MS;
export const DISCARD_REVEAL_DURATION_MS = 2400;
// pause between the bot's decisions so the human can follow along
const BOT_STEP_DELAY = 700;
// pause after the turn changes before the turn-start draw, so the floating
// turn indicator is recognizable first
const TURN_START_DRAW_DELAY = 600;
// how long a played card's exit animation lasts (must match `card-leave` in
// src/App.css)
const CARD_EXIT_DURATION_MS = 600;
// per-player turn timer: each player has a 2-minute bank that only depletes
// while it is their turn; running out loses the game
const TIME_BANK_MS = 2 * 60 * 1000;
const TIMER_TICK_MS = 250;

export type GameUiState = {
    currentPlayer: number;
    gameOver: boolean;
    winner: number | null;
    draw: boolean;
    hand1: Card[];
    hand2: Card[];
    deckCount: number;
    discardCount: number;
    board: (number | null)[][];
    lockedCells: string[];
    targeting: boolean;
    targetingCardId: string | null;
    cardLockedPlayer: number | null;
    cardPlayedThisTurn: boolean;
    chainableCardId: string | null;
    cardDiscardedThisTurn: boolean;
    leavingCardId: string | null;
    gameMode: 'pvp' | 'bot' | 'online';
    botPlayer: number | null;
    // which side this client controls. null in local 2-player (both players
    // share the screen); 1 vs the bot; the assigned side in online matches.
    localPlayer: number | null;
    playerNames: Record<number, string>;
};

type GameCanvasProps = {
    onStateChange?: (state: GameUiState) => void;
    onReady?: (game: Phaser.Game) => void;
    gameMode?: 'pvp' | 'bot' | 'online';
    firstPlayer?: number;
    // online-multiplayer inputs: the server-shared deck + initial hands, this
    // client's side, the action relay, and display names per side
    initialDeck?: Card[];
    initialHands?: Record<number, Card[]>;
    localPlayer?: number | null;
    sendAction?: (action: OnlineAction) => void;
    playerNames?: Record<number, string>;
};

const GameCanvas = ({ onStateChange, onReady, gameMode, firstPlayer, initialDeck, initialHands, localPlayer, sendAction, playerNames }: GameCanvasProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const onStateChangeRef = useRef(onStateChange);
    const onReadyRef = useRef(onReady);
    const gameModeRef = useRef(gameMode);
    const firstPlayerRef = useRef(firstPlayer);
    const initialDeckRef = useRef(initialDeck);
    const initialHandsRef = useRef(initialHands);
    const localPlayerRef = useRef(localPlayer);
    const sendActionRef = useRef(sendAction);
    const playerNamesRef = useRef(playerNames);

    onStateChangeRef.current = onStateChange;
    onReadyRef.current = onReady;
    gameModeRef.current = gameMode;
    firstPlayerRef.current = firstPlayer;
    initialDeckRef.current = initialDeck;
    initialHandsRef.current = initialHands;
    localPlayerRef.current = localPlayer;
    sendActionRef.current = sendAction;
    playerNamesRef.current = playerNames;

    useEffect(() => {
        const config: Phaser.Types.Core.GameConfig = {
            type: Phaser.AUTO,
            width: 800,
            height: 800,
            parent: containerRef.current as HTMLElement,
            transparent: true,
            scene: {
                preload: preload,
                create: create,
            },
        };

        const game = new Phaser.Game(config);
        game.registry.set('uiStateHandler', (state: GameUiState) => onStateChangeRef.current?.(state));
        game.registry.set('gameMode', gameModeRef.current ?? 'pvp');
        game.registry.set('firstPlayer', firstPlayerRef.current ?? 1);
        game.registry.set('initialDeck', initialDeckRef.current ?? null);
        game.registry.set('initialHands', initialHandsRef.current ?? null);
        // `network` is non-null only in online matches: it carries this client's
        // side and the action relay. Local modes leave it null.
        game.registry.set(
            'network',
            localPlayerRef.current !== null && sendActionRef.current
                ? { localPlayer: localPlayerRef.current, sendAction: sendActionRef.current }
                : null
        );
        game.registry.set('playerNames', playerNamesRef.current ?? null);
        onReadyRef.current?.(game);

        return () => {
            game.destroy(true);
        };
    }, []);

    return (
        <div ref={containerRef}></div>
    );
};

export default GameCanvas;

const preload = function (this: Phaser.Scene) {
    const frameWidth = 246;
    const frameHeight = 262;

    this.load.image('myImage', baseSlineSprite);

    for (let i = 0; i < numberOfPiecePerPlayer; i++) {
        this.load.spritesheet(`player01_piece-0${i}`, pieceSprite01, { frameWidth: frameWidth, frameHeight: frameHeight });
        this.load.spritesheet(`player02_piece-0${i}`, pieceSprite02, { frameWidth: frameWidth, frameHeight: frameHeight });
    }

    for (let i = 0; i < numberOfPiecePerPlayer; i++) {
        this.load.spritesheet(`dropzone-${i}`, dropzoneSprite, { frameWidth: frameWidth, frameHeight: frameHeight });
    }
};

const create = function (this: Phaser.Scene) {
    const canvasWidth = this.cameras.main.width;
    const canvasHeight = this.cameras.main.height;

    const linePadding = 200;
    const pieceDisplayWidth = 100;
    const pieceDisplayHeight = 105;

    const emitUiState = this.registry.get('uiStateHandler') as ((state: GameUiState) => void) | undefined;

    // game mode: read from the registry (set by React before the scene boots)
    const gameMode = (this.registry.get('gameMode') as 'pvp' | 'bot' | 'online') ?? 'pvp';
    // in vs-bot mode, Player 2 (top) is controlled by the bot
    const botPlayer: number | null = gameMode === 'bot' ? 2 : null;
    // who moves first (random for local modes, decided by the server for
    // online/invite matches)
    const firstPlayer = (this.registry.get('firstPlayer') as number | undefined) ?? 1;

    // ---- online multiplayer wiring (set by React before the scene boots) ----
    // `network` is non-null only in online matches; the server relays every
    // action back to both clients (the sender included) so both apply the same
    // sequence and keep their simulations in sync
    const network = this.registry.get('network') as
        | { localPlayer: number; sendAction: (action: OnlineAction) => void }
        | null;
    const isOnline = network !== null;
    // server-shared deck + already-dealt hands; both clients start from them
    const initialDeck = (this.registry.get('initialDeck') as Card[] | null) ?? null;
    const initialHands = (this.registry.get('initialHands') as Record<number, Card[]> | null) ?? null;
    // which side this client controls: null = local 2-player (everyone may
    // interact), 1 = vs-bot / online side 1, 2 = online side 2
    const localPlayer: number | null = isOnline
        ? network.localPlayer
        : gameMode === 'pvp'
            ? null
            : 1;
    const playerNames = (this.registry.get('playerNames') as Record<number, string> | null) ?? null;

    // Both players' tables are on the bottom side: side 1 keeps the canonical
    // board (their pieces start on the bottom row), while side 2 sees the board
    // flipped so their pieces (canonically on the top row) appear on the bottom
    // too. World/game coordinates stay canonical - only the RENDERING positions
    // of pieces, dropzones, highlights and locks flip. Sprites themselves stay
    // upright (no camera rotation, which would also flip the piece art).
    const flipped = isOnline && network.localPlayer === 2;

    // draw baseline image
    const baseLineImage = this.add.image(linePadding / 2, linePadding / 2, 'myImage');
    baseLineImage.setDisplaySize(canvasWidth - linePadding, canvasHeight - linePadding);
    baseLineImage.setOrigin(0);

    const board = new Board(baseLineImage);

    // display position of a canonical board cell. For side 2 the board is
    // flipped 180 degrees: canonical row 0 (their pieces) renders where row 2
    // normally sits, canonical col 0 renders where col 2 normally sits.
    const cellCoords = (row: number, col: number) => {
        const r = flipped ? 2 - row : row;
        const c = flipped ? 2 - col : col;
        return board.getPosition(r, c).coordinates;
    };

    // highlight layers (gameplay feedback only - the scene renders no UI):
    // 1) move/selection/targeting highlights
    const highlightGraphics = this.add.graphics();
    // 2) locked-cell markers
    const lockGraphics = this.add.graphics();

    const dropzones: Phaser.GameObjects.Sprite[] = [];
    const pieces: Phaser.GameObjects.Sprite[] = [];

    // draw dropzones
    for (let row = 0; row < numberOfPiecePerPlayer; row++) {
        for (let col = 0; col < numberOfPiecePerPlayer; col++) {
            const dropzonePosition = cellCoords(row, col);

            const dropzone = this.add.sprite(dropzonePosition.x, dropzonePosition.y, `dropzone-${row}`).setInteractive();
            board.positions[row][col].dropzone = dropzone;
            dropzones.push(dropzone);

            // the dropzone keeps its CANONICAL matrix position; the game logic
            // always reasons in canonical coordinates, only the rendering flips
            dropzone.setData('matrixPosition', [row, col]);
            dropzone.setDisplaySize(pieceDisplayWidth, pieceDisplayHeight);
        }
    }

    // draw game pieces
    for (let col = 0; col < numberOfPiecePerPlayer; col++) {
        // Player 1 is on the bottom row, Player 2 on the top row.
        const positionPieceP1 = cellCoords(2, col);
        const positionPieceP2 = cellCoords(0, col);

        // player 01 pieces (bottom row)
        const pieceP1 = this.add.sprite(positionPieceP1.x, positionPieceP1.y, `player01_piece-0${col}`).setInteractive();
        pieceP1.setData('player', 1);
        board.positions[2][col].occupant = pieceP1;
        pieces.push(pieceP1);
        pieceP1.setDisplaySize(pieceDisplayWidth, pieceDisplayHeight);

        // player 02 pieces (top row)
        const pieceP2 = this.add.sprite(positionPieceP2.x, positionPieceP2.y, `player02_piece-0${col}`).setInteractive();
        pieceP2.setData('player', 2);
        board.positions[0][col].occupant = pieceP2;
        pieces.push(pieceP2);
        pieceP2.setDisplaySize(pieceDisplayWidth, pieceDisplayHeight);
    }

    // ---- game state ----
    // Player 1 is on the bottom; the pre-game announcement shows who moves first.
    let currentPlayer = 1;
    let moveNumber = 1;
    let gameOver = false;
    let draw = false;
    // the player who won the game. Set on a win by move and on a timeout. On a
    // timeout the CURRENT player is the loser, so pushState must not report
    // `currentPlayer` as the winner.
    let gameWinner: number | null = null;
    let selectedPiece: Phaser.GameObjects.Sprite | null = null;
    const originalPos = { x: 0, y: 0 };

    // ---- card state ----
    let deck: Card[] = createDeck(DECK_SIZE);
    const hands: Record<number, Card[]> = { 1: [], 2: [] };
    const discard: Card[] = [];
    let targetingCard: Card | null = null;
    // cardLockOwner is the player who applied CARD LOCK; their opponent is
    // the one whose cards are locked (cannot activate any card) until that
    // opponent's turn ends; null = no card lock active
    let cardLockOwner: number | null = null;
    // one-card-per-turn allowance: reset at the start of each turn and set
    // when a card is played, so at most one card can be activated per turn
    let cardActivatedThisTurn = false;
    // the card just drawn by a DRAW CARD: the only card that may be activated
    // as a chain once the one-card-per-turn allowance is used up
    let chainCardId: string | null = null;
    // voluntary-discard allowance: reset at the start of each turn so at most
    // one card can be voluntarily discarded per turn (before any activation)
    let cardDiscardedThisTurn = false;
    // the card currently animating out of its hand (played card exit); the DOM
    // applies the leaving class to it; null = none
    let leavingCardId: string | null = null;
    // ---- bot (vs-bot mode) ----
    // true while the bot is the active player
    const isBotTurn = () => botPlayer !== null && currentPlayer === botPlayer;
    // assigned below once the bot machinery is defined; startTurn calls it
    let scheduleBotTurn: () => void = () => {};
    // true while a piece is animating to its destination (click or bot move);
    // gates board input and card actions so nothing interrupts the tween
    let pieceMoving = false;
    // while the activation overlay is on screen, board input is locked and
    // the turn does not advance until the overlay has finished playing
    let cardAnimating = false;
    // while a discard reveal animation is on screen, board input and card
    // actions are locked until the animation has finished playing
    let discardAnimating = false;

    // a plain click (pointerdown without movement) must not start a drag
    this.input.dragDistanceThreshold = 8;

    const isLegalTarget = (fromRow: number, fromCol: number, toRow: number, toCol: number): boolean =>
        board.canMove(fromRow, fromCol, toRow, toCol);

    // ---- scene -> React state ----
    const pushState = () => {
        emitUiState?.({
            currentPlayer,
            gameOver,
            winner: gameOver && !draw ? gameWinner : null,
            draw,
            hand1: hands[1],
            hand2: hands[2],
            deckCount: deck.length,
            discardCount: discard.length,
            board: Array.from({ length: 3 }, (_, row) =>
                Array.from({ length: 3 }, (_, col) =>
                    board.positions[row][col].occupant?.getData('player') ?? null
                )
            ),
            lockedCells: board.getLockedCells(),
            targeting: targetingCard !== null,
            targetingCardId: targetingCard ? targetingCard.instanceId : null,
            cardLockedPlayer: cardLockOwner === null ? null : cardLockOwner === 1 ? 2 : 1,
            cardPlayedThisTurn: cardActivatedThisTurn,
            chainableCardId: chainCardId,
            cardDiscardedThisTurn,
            leavingCardId,
            gameMode,
            botPlayer,
            localPlayer: isOnline
                ? (localPlayer as number)
                : gameMode === 'bot'
                    ? 1
                    : null,
            playerNames: playerNames ?? { 1: 'Player 1', 2: 'Player 2' },
        });
    };

    const emitCardDrawn = (card: Card, player: number) => {
        this.game.events.emit('card-drawn', { card, player });
    };

    const emitCardPlayed = (card: Card, player: number) => {
        this.game.events.emit('card-played', { card, player });
    };

    // ---- move history logging ----
    const logEntry = (player: number, action: GameLogEntry['action'], detail: string) => {
        this.game.events.emit('game-log', {
            move: moveNumber,
            player,
            action,
            detail,
            bot: botPlayer !== null && player === botPlayer,
        } as GameLogEntry);
    };

    const drawForPlayer = (player: number, excludeInstanceId?: string): Card | null => {
        // the hand-cap count can exclude a card that is being played (its slot
        // is already freed for the incoming draw)
        const count = hands[player].filter((c) => c.instanceId !== excludeInstanceId).length;
        if (count >= MAX_HAND_SIZE) {
            logEntry(player, 'draw', `hand full (max ${MAX_HAND_SIZE}) - no card drawn`);
            return null;
        }
        const result = drawCard(deck);
        if (result) {
            deck = result.remaining;
            hands[player].push(result.card);
            emitCardDrawn(result.card, player);
            logEntry(player, 'draw', `drew +1 card (${result.card.instanceId})`);
            return result.card;
        }
        return null;
    };

    // which pieces may be moved by this client. A piece can only ever be moved
    // on its OWNER's turn - never while the opponent is active. In local
    // 2-player both sides may move when it is their turn; vs the bot and online
    // only the human's own side may move.
    const canControl = (playerNum: number): boolean => {
        if (playerNum !== currentPlayer) {
            return false;
        }
        if (isOnline) {
            return playerNum === localPlayer;
        }
        if (gameMode === 'bot') {
            return playerNum === 1;
        }
        return true;
    };

    const updateDraggable = () => {
        pieces.forEach((piece) => {
            this.input.setDraggable(
                piece,
                !gameOver &&
                    targetingCard === null &&
                    canControl(piece.getData('player') as number) &&
                    piece.getData('player') !== botPlayer
            );
        });
    };

    // ---- locked-cell markers ----
    const refreshLockMarkers = () => {
        lockGraphics.clear();
        board.getLockedCells().forEach((key) => {
            const [row, col] = key.split(',').map(Number);
            const cell = cellCoords(row, col);
            // shackle
            lockGraphics.lineStyle(5, 0xd81b60, 1);
            lockGraphics.strokeCircle(cell.x, cell.y - 17, 12);
            // body
            lockGraphics.fillStyle(0xd81b60, 1);
            lockGraphics.fillRoundedRect(cell.x - 18, cell.y - 18, 36, 34, 7);
            // keyhole
            lockGraphics.fillStyle(0xffffff, 1);
            lockGraphics.fillCircle(cell.x, cell.y - 4, 5);
            lockGraphics.fillStyle(0xd81b60, 1);
            lockGraphics.fillRect(cell.x - 2, cell.y - 3, 4, 11);
        });
    };

    // ---- turn flow ----
    // A player draws a card only on their odd-numbered turns (1st, 3rd, 5th, ...).
    const turnCounts: Record<number, number> = { 1: 0, 2: 0 };

    // ---- per-player turn timer ----
    // each player's bank is charged only while it is their turn; reaching zero loses
    const timeBank: Record<number, number> = { 1: TIME_BANK_MS, 2: TIME_BANK_MS };
    let turnStartedAt: number | null = null;

    const startTurn = (player: number) => {
        // the first call sets the active player from the pre-game decision
        // (random for local modes, server-chosen for online); later calls are
        // already preceded by advanceTurn, which sets currentPlayer before
        // calling startTurn
        currentPlayer = player;
        turnCounts[player] += 1;
        // start the active player's turn timer
        turnStartedAt = this.time.now;
        // a fresh turn resets the per-turn card allowances
        cardActivatedThisTurn = false;
        chainCardId = null;
        cardDiscardedThisTurn = false;
        updateDraggable();
        refreshLockMarkers();
        // show the new turn indicator immediately...
        pushState();
        // ...then pause before the odd-turn draw so the active player is
        // recognizable before the card slides in
        if (turnCounts[player] % 2 === 1) {
            this.time.delayedCall(TURN_START_DRAW_DELAY, () => {
                if (gameOver || currentPlayer !== player) {
                    return;
                }
                drawForPlayer(player);
                pushState();
            });
        }
        // in vs-bot mode the bot plays its own turn automatically (after the draw)
        if (botPlayer !== null && player === botPlayer) {
            this.time.delayedCall(TURN_START_DRAW_DELAY + BOT_STEP_DELAY, scheduleBotTurn);
        }
    };

    const advanceTurn = () => {
        // charge the departing player's time; running out loses the game
        if (!gameOver && turnStartedAt !== null) {
            const elapsed = this.time.now - turnStartedAt;
            timeBank[currentPlayer] = Math.max(0, timeBank[currentPlayer] - elapsed);
            if (timeBank[currentPlayer] <= 0) {
                handleTimeout(currentPlayer);
                return;
            }
        }
        // end of turn: clear any leftover selection and its highlights
        clearSelection();

        // a card lock covers exactly the opponent's next turn: it expires when
        // the locked player's turn ends, before the turn returns to the owner
        if (cardLockOwner !== null && currentPlayer !== cardLockOwner) {
            cardLockOwner = null;
            logEntry(currentPlayer, 'card-unlock', 'card lock expired');
        }

        currentPlayer = currentPlayer === 1 ? 2 : 1;
        if (currentPlayer === 1) {
            moveNumber += 1;
        }

        // when the turn returns to a player, their locked cells complete their
        // one-turn cycle and are unlocked before their legal moves are checked
        const expiredLocks = board.getCellsLockedBy(currentPlayer);
        board.unlockByOwner(currentPlayer);
        expiredLocks.forEach((key) => {
            const [row, col] = key.split(',').map(Number);
            logEntry(currentPlayer, 'unlock', `${cellName(row, col)} unlocked`);
        });

        // if the player whose turn it is has no legal moves, the game ends in a draw
        if (!board.hasLegalMove(currentPlayer)) {
            draw = true;
            gameOver = true;
            logEntry(currentPlayer, 'draw-game', 'no legal moves — game ends in a draw');
            updateDraggable();
            clearSelection();
            refreshLockMarkers();
            pushState();
            return;
        }

        startTurn(currentPlayer);
    };

    // ---- legal move highlighting (board lines + cells + selection ring) ----
    let pulseTween: Phaser.Tweens.Tween | null = null;

    const strokeSegments = (fromRow: number, fromCol: number) => {
        const from = cellCoords(fromRow, fromCol);
        board.positions[fromRow][fromCol].possibleMoves.forEach(([toRow, toCol]) => {
            const to = cellCoords(toRow, toCol);
            highlightGraphics.lineBetween(from.x, from.y, to.x, to.y);
        });
    };

    const drawLegalHighlights = (fromRow: number, fromCol: number) => {
        highlightGraphics.clear();

        // soft glow pass
        highlightGraphics.lineStyle(16, 0xffc107, 0.35);
        strokeSegments(fromRow, fromCol);

        // solid pass
        highlightGraphics.lineStyle(7, 0xffc107, 1);
        strokeSegments(fromRow, fromCol);

        // destination cell markers
        board.positions[fromRow][fromCol].possibleMoves.forEach(([toRow, toCol]) => {
            const to = cellCoords(toRow, toCol);
            highlightGraphics.fillStyle(0xffc107, 0.55);
            highlightGraphics.fillRoundedRect(to.x - 27, to.y - 28, 54, 56, 12);
        });

        // selection ring around the piece's cell
        const from = cellCoords(fromRow, fromCol);
        highlightGraphics.lineStyle(4, 0xffc107, 1);
        highlightGraphics.strokeRoundedRect(from.x - 54, from.y - 57, 108, 114, 12);

        if (!pulseTween) {
            pulseTween = this.tweens.add({
                targets: highlightGraphics,
                alpha: { from: 0.55, to: 1 },
                duration: 550,
                yoyo: true,
                repeat: -1,
            });
        }
    };

    // ---- card targeting highlight (eligible empty + unlocked cells) ----
    const drawTargetingHighlights = () => {
        highlightGraphics.clear();
        for (let row = 0; row < 3; row++) {
            for (let col = 0; col < 3; col++) {
                if (board.isPositionOccupied(row, col) || board.isCellLocked(row, col)) {
                    continue;
                }
                const cell = cellCoords(row, col);
                highlightGraphics.lineStyle(5, 0x66bb6a, 1);
                highlightGraphics.strokeRoundedRect(cell.x - 38, cell.y - 40, 76, 80, 14);
                highlightGraphics.fillStyle(0x66bb6a, 0.4);
                highlightGraphics.fillRoundedRect(cell.x - 38, cell.y - 40, 76, 80, 14);
            }
        }
    };

    const clearLegalHighlights = () => {
        if (pulseTween) {
            pulseTween.stop();
            pulseTween = null;
        }
        highlightGraphics.setAlpha(1);
        highlightGraphics.clear();
    };

    const clearSelection = () => {
        selectedPiece = null;
        clearLegalHighlights();
    };

    const cancelTargeting = () => {
        targetingCard = null;
        clearLegalHighlights();
        updateDraggable();
        pushState();
    };

    // ---- card activation helpers ----
    // begin playing a card: mark the one-card-per-turn flag and fire the
    // ACTIVATE overlay; the card stays in the hand until its animations finish
    const beginCardPlay = (card: Card) => {
        cardActivatedThisTurn = true;
        emitCardPlayed(card, currentPlayer);
    };

    // remove a played card from the hand into the discard
    const removeCardFromHand = (card: Card) => {
        const hand = hands[currentPlayer];
        const index = hand.findIndex((c) => c.instanceId === card.instanceId);
        if (index >= 0) {
            hand.splice(index, 1);
        }
        discard.push(card);
    };

    // after a card's effect has been applied: mark the played card as leaving
    // (the DOM plays its exit animation), then remove it and run `onDone`.
    // `exitMs` covers the exit animation (THROW waits for its reveal)
    const finishPlayedCard = (card: Card, onDone: () => void, exitMs = CARD_EXIT_DURATION_MS) => {
        leavingCardId = card.instanceId;
        pushState();
        this.time.delayedCall(exitMs, () => {
            leavingCardId = null;
            removeCardFromHand(card);
            cardAnimating = false;
            pushState();
            onDone();
        });
    };

    // ---- LOCK POSITION card ----
    // core lock execution: used by the human's targeting flow and directly by
    // the bot (which has no targeting UI). The lock is applied only after the
    // ACTIVATE animation; LOCK POSITION consumes the turn
    const commitLockCard = (card: Card, row: number, col: number, onComplete?: () => void) => {
        cardAnimating = true;
        clearLegalHighlights();
        beginCardPlay(card);
        this.time.delayedCall(CARD_ACTIVATE_DURATION_MS, () => {
            // the locked cell + padlock marker appear only after the animation
            board.lockCell(row, col, currentPlayer);
            logEntry(currentPlayer, 'lock', `LOCK ${cellName(row, col)}`);
            refreshLockMarkers();
            finishPlayedCard(card, () => {
                advanceTurn();
                onComplete?.();
            });
        });
    };

    const commitLock = (row: number, col: number) => {
        if (!targetingCard || cardAnimating) {
            return;
        }
        const card = targetingCard;
        targetingCard = null;
        if (isOnline && network) {
            // the acting player picks the target locally, then the lock is
            // committed on both clients once the server relays the action
            clearLegalHighlights();
            pushState();
            network.sendAction({ type: 'lock', instanceId: card.instanceId, row, col });
            return;
        }
        commitLockCard(card, row, col);
    };

    // ---- CARD LOCK card: effect applied after the activation animation ----
    const applyCardLock = (card: Card, onComplete?: () => void) => {
        cardAnimating = true;
        clearLegalHighlights();
        beginCardPlay(card);
        this.time.delayedCall(CARD_ACTIVATE_DURATION_MS, () => {
            // the opponent's cards become locked only after the animation
            cardLockOwner = currentPlayer;
            logEntry(currentPlayer, 'card-lock', 'CARD LOCK applied - opponent cannot activate cards');
            finishPlayedCard(card, () => {
                updateDraggable();
                onComplete?.();
            });
        });
    };

    // ---- THROW CARD card: force the opponent to discard one card ----
    // `forcedTargetId` is used in online matches: the acting player picks the
    // random target and the server relays it, so both clients discard the exact
    // same card. Locally (pvp/bot) the target is picked randomly here.
    const applyThrowCard = (card: Card, onComplete?: () => void, forcedTargetId?: string) => {
        const opponent = currentPlayer === 1 ? 2 : 1;
        const opponentHand = hands[opponent];
        if (opponentHand.length === 0) {
            return;
        }
        let discarded: Card;
        if (forcedTargetId) {
            const found = opponentHand.find((c) => c.instanceId === forcedTargetId);
            if (!found) {
                return;
            }
            discarded = found;
        } else {
            const index = Math.floor(Math.random() * opponentHand.length);
            discarded = opponentHand[index];
        }
        logEntry(currentPlayer, 'throw', `THROW forced P${opponent} to discard ${discarded.id}`);

        cardAnimating = true;
        clearLegalHighlights();
        beginCardPlay(card);
        this.time.delayedCall(CARD_ACTIVATE_DURATION_MS, () => {
            // the opponent's card leaves + is revealed only after the animation
            const hand = hands[opponent];
            const i = hand.indexOf(discarded);
            if (i >= 0) {
                hand.splice(i, 1);
                discard.push(discarded);
            } else {
                discard.push(discarded);
            }
            pushState();
            this.game.events.emit('card-discarded', { card: discarded, player: opponent, delay: 0 });

            // the played THROW CARD stays in the hand through the discard reveal
            finishPlayedCard(card, () => {
                updateDraggable();
                onComplete?.();
            }, DISCARD_REVEAL_DURATION_MS);
        });
    };

    // ---- DRAW CARD card: draw 1 only after the activation animation ----
    const applyDrawOne = (card: Card, onComplete?: () => void) => {
        logEntry(currentPlayer, 'draw-card', 'DRAW CARD activated');
        cardAnimating = true;
        clearLegalHighlights();
        beginCardPlay(card);
        this.time.delayedCall(CARD_ACTIVATE_DURATION_MS, () => {
            // the drawn card joins the hand only after the animation; the played
            // DRAW CARD is excluded from the hand-cap count because its slot is
            // already freed for the incoming draw
            const drawn = drawForPlayer(currentPlayer, card.instanceId);
            chainCardId = drawn ? drawn.instanceId : null;
            pushState();
            finishPlayedCard(card, () => {
                updateDraggable();
                onComplete?.();
            });
        });
    };

    // ---- shared move logic (click, drag, and bot) ----
    // `animate` tweens the piece to its destination (used by the bot so its
    // moves are not instant); drag-and-drop and click moves stay instant
    const commitMove = (fromRow: number, fromCol: number, toRow: number, toCol: number, animate = false) => {
        const movingPiece = board.positions[fromRow][fromCol].occupant;
        const targetDropzone = board.positions[toRow][toCol].dropzone;
        if (!movingPiece || !targetDropzone) {
            return;
        }

        const applyMove = () => {
            movingPiece.setAlpha(1);
            board.positions[toRow][toCol].occupant = movingPiece;
            board.positions[fromRow][fromCol].occupant = null;
            movingPiece.x = targetDropzone.x;
            movingPiece.y = targetDropzone.y;

            logEntry(currentPlayer, 'move', `${cellName(fromRow, fromCol)} → ${cellName(toRow, toCol)}`);

            // check for a winning line
            if (board.hasWon(currentPlayer)) {
                gameOver = true;
                gameWinner = currentPlayer;
                logEntry(currentPlayer, 'win', 'wins the game');
                board.getPlayerPieces(currentPlayer).forEach(({ row, col }) => {
                    board.positions[row][col].occupant?.setTint(0x66bb6a);
                });
                updateDraggable();
                clearSelection();
                pushState();
                return;
            }

            advanceTurn();
        };

        if (animate) {
            pieceMoving = true;
            this.tweens.add({
                targets: movingPiece,
                x: targetDropzone.x,
                y: targetDropzone.y,
                duration: 350,
                ease: 'Cubic.easeInOut',
                onComplete: () => {
                    pieceMoving = false;
                    applyMove();
                },
            });
            return;
        }
        applyMove();
    };

    // user-initiated move. Online matches send the action to the server and
    // wait for its echo (so both clients apply the same sequence); local modes
    // commit the move immediately. `animate` keeps drag-and-drop instant and
    // click moves tweened, matching the pre-existing behavior.
    const requestMove = (fromRow: number, fromCol: number, toRow: number, toCol: number, animate = true) => {
        if (isOnline && network) {
            pieceMoving = true;
            network.sendAction({ type: 'move', from: [fromRow, fromCol], to: [toRow, toCol] });
            return;
        }
        commitMove(fromRow, fromCol, toRow, toCol, animate);
    };

    // ---- click-to-move (and card targeting cancellation) ----
    pieces.forEach((piece) => {
        piece.on('pointerdown', () => {
            if (gameOver || cardAnimating || discardAnimating || isBotTurn() || pieceMoving) {
                return;
            }
            if (targetingCard) {
                cancelTargeting();
                return;
            }
            if (!canControl(piece.getData('player') as number)) {
                return;
            }
            if (selectedPiece === piece) {
                clearSelection();
                return;
            }
            selectedPiece = piece;
            const { row, col } = board.getIndexOfPiece(piece);
            board.lastPiecePosition = { row, col };
            drawLegalHighlights(row, col);
        });

        piece.on('pointerover', () => {
            this.input.setDefaultCursor('pointer');
        });
        piece.on('pointerout', () => {
            this.input.setDefaultCursor('default');
        });
    });

    dropzones.forEach((dropzone) => {
        dropzone.on('pointerdown', () => {
            if (gameOver || cardAnimating || discardAnimating || isBotTurn() || pieceMoving) {
                return;
            }
            if (targetingCard) {
                const [toRow, toCol] = dropzone.getData('matrixPosition') as [number, number];
                if (!board.isPositionOccupied(toRow, toCol) && !board.isCellLocked(toRow, toCol)) {
                    commitLock(toRow, toCol);
                } else {
                    cancelTargeting();
                }
                return;
            }
            if (!selectedPiece) {
                return;
            }
            // only a piece this client controls may be moved
            if (!canControl(selectedPiece.getData('player') as number)) {
                clearSelection();
                return;
            }
            const from = board.getIndexOfPiece(selectedPiece);
            const [toRow, toCol] = dropzone.getData('matrixPosition') as [number, number];
            if (isLegalTarget(from.row, from.col, toRow, toCol)) {
                // animate click moves (drag-and-drop stays instant)
                requestMove(from.row, from.col, toRow, toCol, true);
            } else {
                clearSelection();
            }
        });

        dropzone.on('pointerover', () => {
            this.input.setDefaultCursor('pointer');
        });
        dropzone.on('pointerout', () => {
            this.input.setDefaultCursor('default');
        });
    });

    // clicking empty board space cancels the selection (or targeting)
    baseLineImage.setInteractive();
    baseLineImage.on('pointerdown', () => {
        if (gameOver || cardAnimating || discardAnimating || isBotTurn() || pieceMoving) {
            return;
        }
        if (targetingCard) {
            cancelTargeting();
            return;
        }
        clearSelection();
    });

    // ---- drag-and-drop ----
    this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Sprite) => {
        if (gameOver || cardAnimating || discardAnimating || targetingCard !== null || isBotTurn() || pieceMoving || !canControl(gameObject.getData('player') as number)) {
            return;
        }

        gameObject.setAlpha(0.5);
        originalPos.x = gameObject.x;
        originalPos.y = gameObject.y;
        board.lastPiecePosition = board.getIndexOfPiece(gameObject);
        selectedPiece = gameObject;
        drawLegalHighlights(board.lastPiecePosition.row, board.lastPiecePosition.col);
    });

    this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Sprite, dragX: number, dragY: number) => {
        if (gameOver || discardAnimating || isBotTurn() || pieceMoving) {
            return;
        }

        gameObject.x = dragX;
        gameObject.y = dragY;
    });

    this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Sprite) => {
        if (gameOver || cardAnimating || discardAnimating || isBotTurn() || pieceMoving) {
            return;
        }

        gameObject.setAlpha(1);

        const from = board.lastPiecePosition;
        const collided = dropzones.filter((dropzone) => checkOverLap(dropzone, gameObject));

        if (collided.length > 0) {
            const [toRow, toCol] = collided[0].getData('matrixPosition') as [number, number];

            if (isLegalTarget(from.row, from.col, toRow, toCol)) {
                requestMove(from.row, from.col, toRow, toCol, false);
                return;
            }
        }

        // illegal move or dropped outside a dropzone: send the piece back
        gameObject.x = originalPos.x;
        gameObject.y = originalPos.y;
        clearSelection();
    });

    // ---- card activation from the DOM hands ----
    // Apply a card activation locally (shared by local play, the bot, and the
    // relayed echo of an online action).
    const applyCardActivate = (instanceId: string) => {
        // a locked player cannot activate any card, and at most one card may
        // be activated per turn - except the card just drawn by a DRAW CARD,
        // which is chainable (defense in depth behind the DOM gate)
        const isChainable = chainCardId !== null && instanceId === chainCardId;
        if (
            gameOver ||
            cardAnimating ||
            discardAnimating ||
            pieceMoving ||
            (cardActivatedThisTurn && !isChainable) ||
            (cardLockOwner !== null && currentPlayer !== cardLockOwner)
        ) {
            return;
        }
        const hand = hands[currentPlayer];
        const card = hand.find((c) => c.instanceId === instanceId);
        if (!card) {
            return;
        }
        // activating a card cancels any piece selection first, so the
        // targeting highlight is not hidden behind a selected piece's lines
        if (selectedPiece) {
            clearSelection();
        }
        // CARD LOCK applies immediately (no targeting); THROW CARD forces a
        // random opponent discard (no targeting); DRAW CARD draws 1 and makes
        // the drawn card chainable; LOCK POSITION enters cell targeting
        if (card.id === 'card-lock') {
            applyCardLock(card);
            return;
        }
        if (card.id === 'throw-card') {
            // nothing to throw: reject (defense in depth behind the DOM gate)
            if (hands[currentPlayer === 1 ? 2 : 1].length === 0) {
                return;
            }
            applyThrowCard(card);
            return;
        }
        if (card.id === 'draw-one') {
            applyDrawOne(card);
            return;
        }
        targetingCard = card;
        drawTargetingHighlights();
        updateDraggable();
        pushState();
    };

    // DOM card menu -> scene. Online matches turn the local intent into a
    // relayed action instead of applying it here (the echo applies it on both
    // clients, keeping the two simulations in sync).
    const onCardActivate = (instanceId: string) => {
        if (isOnline && network && currentPlayer === localPlayer) {
            const hand = hands[currentPlayer];
            const card = hand.find((c) => c.instanceId === instanceId);
            if (!card) {
                return;
            }
            if (card.id === 'throw-card') {
                const opponent = currentPlayer === 1 ? 2 : 1;
                const opponentHand = hands[opponent];
                if (opponentHand.length === 0) {
                    return;
                }
                // pick the random target here and send it, so both clients
                // discard the exact same card on the echo
                const targetId = opponentHand[Math.floor(Math.random() * opponentHand.length)].instanceId;
                network.sendAction({ type: 'throw', instanceId, targetId });
                return;
            }
            if (card.id === 'lock-position') {
                // enter targeting locally; the cell choice is sent as a `lock`
                // action when the player clicks a dropzone
                if (gameOver || cardAnimating || discardAnimating || pieceMoving) {
                    return;
                }
                if (selectedPiece) {
                    clearSelection();
                }
                targetingCard = card;
                drawTargetingHighlights();
                updateDraggable();
                pushState();
                return;
            }
            network.sendAction({ type: 'card-activate', instanceId });
            return;
        }
        applyCardActivate(instanceId);
    };
    this.game.events.on('card-activate', onCardActivate);

    // ---- voluntary discard (shared by the human's Discard action and the bot) ----
    // `onDone` fires only after the discard reveal animation has finished, so
    // succeeding actions (the bot's next decision, the human's next input) wait
    const discardCardFromHand = (card: Card, onDone?: () => void) => {
        const hand = hands[currentPlayer];
        const index = hand.indexOf(card);
        if (index < 0) {
            onDone?.();
            return;
        }
        hand.splice(index, 1);
        discard.push(card);
        cardDiscardedThisTurn = true;
        logEntry(currentPlayer, 'discard', `discarded ${card.id}`);
        // reveal the discarded card with the discard animation; a voluntary
        // discard has no activation overlay first, so the reveal starts now
        this.game.events.emit('card-discarded', { card, player: currentPlayer, delay: 0 });
        pushState();

        // succeeding actions must wait until the discard animation is done
        // (duration must match `card-discard` in src/App.css)
        discardAnimating = true;
        this.time.delayedCall(DISCARD_REVEAL_DURATION_MS, () => {
            discardAnimating = false;
            onDone?.();
        });
    };

    const applyCardDiscard = (instanceId: string) => {
        if (gameOver || cardAnimating || discardAnimating || pieceMoving || targetingCard !== null || cardActivatedThisTurn || cardDiscardedThisTurn) {
            return;
        }
        const hand = hands[currentPlayer];
        const card = hand.find((c) => c.instanceId === instanceId);
        if (!card) {
            return;
        }
        discardCardFromHand(card);
    };

    const onCardDiscardRequested = (instanceId: string) => {
        if (isOnline && network && currentPlayer === localPlayer) {
            if (gameOver || cardAnimating || discardAnimating || pieceMoving || targetingCard !== null || cardActivatedThisTurn || cardDiscardedThisTurn) {
                return;
            }
            const hand = hands[currentPlayer];
            const card = hand.find((c) => c.instanceId === instanceId);
            if (!card) {
                return;
            }
            network.sendAction({ type: 'discard', instanceId });
            return;
        }
        applyCardDiscard(instanceId);
    };
    this.game.events.on('card-discard-requested', onCardDiscardRequested);

    // ---- online actions: apply the server-relayed echo on this client ----
    const onOnlineAction = (action: OnlineAction) => {
        if (!isOnline) {
            return;
        }
        switch (action.type) {
            case 'move':
                // the tween is a no-op for the drag-and-drop actor (the piece
                // already sits on the target cell); the opponent sees it animate
                commitMove(action.from[0], action.from[1], action.to[0], action.to[1], true);
                break;
            case 'card-activate':
                applyCardActivate(action.instanceId);
                break;
            case 'throw': {
                const hand = hands[currentPlayer];
                const card = hand.find((c) => c.instanceId === action.instanceId);
                if (card) {
                    applyThrowCard(card, undefined, action.targetId);
                }
                break;
            }
            case 'lock': {
                const hand = hands[currentPlayer];
                const card = hand.find((c) => c.instanceId === action.instanceId);
                if (card) {
                    commitLockCard(card, action.row, action.col);
                }
                break;
            }
            case 'discard':
                applyCardDiscard(action.instanceId);
                break;
            case 'timeout':
                handleTimeout(action.player);
                break;
            default:
                break;
        }
    };
    this.game.events.on('online-action', onOnlineAction);

    // restart via the DOM restart control (never in online matches - the other
    // player is not part of a local restart)
    const onRestartRequested = () => {
        if (isOnline) {
            return;
        }
        logEntry(0, 'restart', 'Game restarted');
        this.scene.restart();
    };
    this.game.events.on('restart-requested', onRestartRequested);

    // ---- bot orchestration (vs-bot mode) ----
    const buildBotState = (): BotState => {
        const opp = botPlayer === 1 ? 2 : 1;
        const legalMoves: BotMove[] = [];
        if (botPlayer !== null) {
            for (const { row, col } of board.getPlayerPieces(botPlayer)) {
                for (const [toRow, toCol] of board.positions[row][col].possibleMoves) {
                    if (board.canMove(row, col, toRow, toCol)) {
                        legalMoves.push({ from: [row, col], to: [toRow, toCol] });
                    }
                }
            }
        }
        return {
            player: botPlayer as number,
            board: board.positions.map((r) =>
                r.map((p) => (p.occupant ? (p.occupant.getData('player') as number) : null))
            ),
            lockedCells: board.getLockedCells(),
            hand: botPlayer !== null ? hands[botPlayer] : [],
            opponentHandCount: botPlayer !== null ? hands[opp].length : 0,
            legalMoves,
            cardLockOwner,
            chainableCardId: chainCardId,
            canActivate: !cardActivatedThisTurn || chainCardId !== null,
            canDiscard: !cardDiscardedThisTurn && !cardActivatedThisTurn,
        };
    };

    const doBotDiscard = (card: Card, onDone?: () => void) => {
        discardCardFromHand(card, onDone);
    };

    const doBotActivation = (decision: ActivationDecision) => {
        const card = decision.card;
        if (card.id === 'lock-position' && decision.lockTarget) {
            // LOCK POSITION consumes the turn; the turn passes after the sequence
            botPhase = 'done';
            botTurnActive = false;
            commitLockCard(card, decision.lockTarget.row, decision.lockTarget.col);
            return;
        }
        if (card.id === 'card-lock') {
            applyCardLock(card, () => botContinue());
        } else if (card.id === 'throw-card') {
            applyThrowCard(card, () => botContinue());
        } else if (card.id === 'draw-one') {
            applyDrawOne(card, () => botContinue());
        }
    };

    const doBotMove = (move: BotMove) => {
        // animate the piece so the bot's move is not instant
        commitMove(move.from[0], move.from[1], move.to[0], move.to[1], true);
    };

    let botPhase: 'discard' | 'activate' | 'move' | 'done' = 'done';
    let botTurnActive = false;

    const botContinue = () => {
        if (botPlayer === null || gameOver || currentPlayer !== botPlayer) {
            botTurnActive = false;
            botPhase = 'done';
            return;
        }
        if (!botTurnActive) {
            return;
        }
        switch (botPhase) {
            case 'discard': {
                botPhase = 'activate';
                const state = buildBotState();
                const card = pickDiscard(state, BEGINNER_ACCURACY);
                const proceed = () => {
                    this.time.delayedCall(BOT_STEP_DELAY, botContinue);
                };
                if (card) {
                    // wait for the discard animation before the next decision
                    doBotDiscard(card, proceed);
                } else {
                    proceed();
                }
                break;
            }
            case 'activate': {
                const state = buildBotState();
                const decision = pickActivation(state, BEGINNER_ACCURACY);
                if (decision) {
                    // keep-turn cards schedule their own continuation
                    doBotActivation(decision);
                } else {
                    botPhase = 'move';
                    this.time.delayedCall(BOT_STEP_DELAY, botContinue);
                }
                break;
            }
            case 'move': {
                botPhase = 'done';
                botTurnActive = false;
                const state = buildBotState();
                const move = pickMove(state, BEGINNER_ACCURACY);
                if (move) {
                    doBotMove(move); // commitMove advances the turn
                } else if (currentPlayer === botPlayer && !gameOver) {
                    advanceTurn(); // no legal move: end the turn safely
                }
                break;
            }
            default:
                botTurnActive = false;
        }
    };

    scheduleBotTurn = () => {
        botTurnActive = true;
        botPhase = 'discard';
        botContinue();
    };
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.game.events.off('restart-requested', onRestartRequested);
        this.game.events.off('card-activate', onCardActivate);
        this.game.events.off('card-discard-requested', onCardDiscardRequested);
        this.game.events.off('online-action', onOnlineAction);
    });

    // ---- per-player turn timer: ticker drives the DOM countdown ----
    const handleTimeout = (player: number) => {
        if (gameOver) {
            return;
        }
        gameOver = true;
        const winner = player === 1 ? 2 : 1;
        gameWinner = winner;
        logEntry(0, 'win', `P${player} ran out of time - P${winner} wins by timeout`);
        board.getPlayerPieces(winner).forEach(({ row, col }) => {
            board.positions[row][col].occupant?.setTint(0x66bb6a);
        });
        updateDraggable();
        clearSelection();
        refreshLockMarkers();
        pushState();
    };

    this.time.addEvent({
        delay: TIMER_TICK_MS,
        loop: true,
        callback: () => {
            if (gameOver || turnStartedAt === null) {
                return;
            }
            const elapsed = this.time.now - turnStartedAt;
            const remaining = timeBank[currentPlayer] - elapsed;
            if (remaining <= 0) {
                timeBank[currentPlayer] = 0;
                if (isOnline && network && currentPlayer === localPlayer) {
                    // the active player's client announces the timeout so both
                    // clients end the game identically
                    network.sendAction({ type: 'timeout', player: currentPlayer });
                } else {
                    handleTimeout(currentPlayer);
                }
                return;
            }
            this.game.events.emit('timer-tick', {
                active: currentPlayer,
                player1: timeBank[1] - (currentPlayer === 1 ? elapsed : 0),
                player2: timeBank[2] - (currentPlayer === 2 ? elapsed : 0),
            });
        },
    });

    // ---- initial deal + first turn (first mover decided before the game:
    // randomly for local modes, by the server for online/invite matches) ----
    logEntry(0, 'start', `Game started (${gameMode === 'bot' ? 'vs bot' : gameMode === 'online' ? 'online' : '2 players'}) — 30-card deck, 1 card dealt to each player, ${firstPlayer === 1 ? 'Player 1' : 'Player 2'} moves first`);
    if (initialDeck && initialHands) {
        // online: the server already shuffled the deck and dealt one card to
        // each player; both clients start from the exact same state
        deck = [...initialDeck];
        hands[1] = [...initialHands[1]];
        hands[2] = [...initialHands[2]];
    } else {
        drawForPlayer(1);
        drawForPlayer(2);
    }
    startTurn(firstPlayer);
};

const checkOverLap = (
    obj01: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
    obj02: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite) => {

    return Phaser.Geom.Rectangle.Area(Phaser.Geom.Rectangle.Intersection(obj01.getBounds(), obj02.getBounds())) > 0;
};
